import React, { useState } from "react";
import useCategoryNavigation, {
  NavMaterial,
} from "../../../Hooks/useCategoryNavigation";
import { useCategories } from "../../../Hooks/useCategories";
import { NAV_ITEMS } from "./navItems";
import { FiChevronDown, FiX } from "../../../icons";
import "../../../Styles/CategoryNav.css";

interface MobileNavProps {
  open: boolean;
  onClose: () => void;
}

/**
 * Mobile navigation drawer. Materials expand into an accordion of categories,
 * which in turn expand into subcategories — no hover cascade.
 */
const MobileNav: React.FC<MobileNavProps> = ({ open, onClose }) => {
  const { data } = useCategories();
  const { goToMaterial, goToCategory, goToSubcategory } =
    useCategoryNavigation();
  const [expandedMaterial, setExpandedMaterial] = useState<NavMaterial | null>(
    null
  );
  const [expandedCategory, setExpandedCategory] = useState<number | null>(null);

  const categories = data?.categories ?? [];

  const close = () => {
    setExpandedMaterial(null);
    setExpandedCategory(null);
    onClose();
  };

  const toggleMaterial = (material: NavMaterial) => {
    setExpandedCategory(null);
    setExpandedMaterial((prev) => (prev === material ? null : material));
  };

  const toggleCategory = (id: number) => {
    setExpandedCategory((prev) => (prev === id ? null : id));
  };

  return (
    <>
      <div
        className={"mnav-backdrop" + (open ? " is-open" : "")}
        onClick={close}
        aria-hidden
      />
      <aside
        className={"mnav-drawer" + (open ? " is-open" : "")}
        role="dialog"
        aria-label="Meni"
        aria-hidden={!open}
      >
        <div className="mnav-header">
          <span className="mnav-title">Meni</span>
          <button
            type="button"
            className="mnav-close"
            onClick={close}
            aria-label="Zatvori meni"
          >
            <FiX />
          </button>
        </div>

        <nav className="mnav-body">
          {NAV_ITEMS.map((item) => {
            const isOpen = expandedMaterial === item.material;
            return (
              <div className="mnav-group" key={item.label}>
                <button
                  type="button"
                  className={"mnav-material" + (isOpen ? " is-open" : "")}
                  onClick={() => {
                    if (!item.hasMenu) {
                      goToMaterial(item.material);
                      close();
                    } else {
                      toggleMaterial(item.material);
                    }
                  }}
                >
                  {item.label}
                  {item.hasMenu && (
                    <FiChevronDown className="mnav-caret" aria-hidden />
                  )}
                </button>

                {item.hasMenu && isOpen && (
                  <div className="mnav-categories">
                    <button
                      type="button"
                      className="mnav-shopall"
                      onClick={() => {
                        goToMaterial(item.material);
                        close();
                      }}
                    >
                      Prikaži sve
                    </button>
                    {categories.map((category) => {
                      const catOpen = expandedCategory === category.id;
                      const hasSubs = category.subcategories.length > 0;
                      return (
                        <div className="mnav-cat" key={category.id}>
                          <div className="mnav-cat-row">
                            <button
                              type="button"
                              className="mnav-cat-name"
                              onClick={() => {
                                goToCategory(item.material, category.id);
                                close();
                              }}
                            >
                              {category.category_name}
                            </button>
                            {hasSubs && (
                              <button
                                type="button"
                                className={
                                  "mnav-cat-toggle" + (catOpen ? " is-open" : "")
                                }
                                onClick={() => toggleCategory(category.id)}
                                aria-label="Prikaži podkategorije"
                              >
                                <FiChevronDown aria-hidden />
                              </button>
                            )}
                          </div>
                          {catOpen && hasSubs && (
                            <ul className="mnav-subs">
                              {category.subcategories.map((sub) => (
                                <li
                                  key={
                                    sub.subcategory_id ?? sub.subcategory_name
                                  }
                                >
                                  <button
                                    type="button"
                                    className="mnav-sub"
                                    onClick={() => {
                                      goToSubcategory(
                                        item.material,
                                        category.id,
                                        sub.subcategory_id
                                      );
                                      close();
                                    }}
                                  >
                                    {sub.subcategory_name}
                                  </button>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </nav>
      </aside>
    </>
  );
};

export default MobileNav;
